import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import { RefreshCw, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

export const TestPage: React.FC = () => {
  const [testResults, setTestResults] = useState<Record<string, any>>({});

  // Teste de health check
  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const response = await api.get('/health');
      return response.data;
    },
    retry: 1,
  });



  const runTests = async () => {
    setTestResults({});
    
    try {
      // Teste 1: Health Check
      const healthResponse = await api.get('/health');
      setTestResults(prev => ({
        ...prev,
        health: {
          success: true,
          data: healthResponse.data,
          timestamp: new Date().toISOString()
        }
      }));

      // Teste 2: Login
      try {
        const loginResponse = await api.post('/auth/login', {
          email: 'admin@cobrancas.com',
          password: 'Admin123!'
        });
        setTestResults(prev => ({
          ...prev,
          login: {
            success: true,
            data: { user: loginResponse.data.data.user.name },
            timestamp: new Date().toISOString()
          }
        }));

        // Teste 3: Kanban (com autenticação)
        try {
          const kanbanResponse = await api.get('/titulos/kanban');
          setTestResults(prev => ({
            ...prev,
            kanban: {
              success: true,
              data: { 
                etapas: kanbanResponse.data.data.etapas?.length || 0,
                kanban: Object.keys(kanbanResponse.data.data.kanban || {}).length
              },
              timestamp: new Date().toISOString()
            }
          }));
        } catch (error: any) {
          setTestResults(prev => ({
            ...prev,
            kanban: {
              success: false,
              error: error.response?.data?.message || error.message,
              timestamp: new Date().toISOString()
            }
          }));
        }

        // Teste 4: Clientes (com autenticação)
        try {
          const clientesResponse = await api.get('/clientes-fornecedores');
          const payload = clientesResponse.data.data;
          const total = Array.isArray(payload)
            ? payload.length
            : (payload?.pagination?.total ?? payload?.items?.length ?? 0);
          setTestResults(prev => ({
            ...prev,
            clientes: {
              success: true,
              data: { 
                total
              },
              timestamp: new Date().toISOString()
            }
          }));
        } catch (error: any) {
          setTestResults(prev => ({
            ...prev,
            clientes: {
              success: false,
              error: error.response?.data?.message || error.message,
              timestamp: new Date().toISOString()
            }
          }));
        }

      } catch (error: any) {
        setTestResults(prev => ({
          ...prev,
          login: {
            success: false,
            error: error.response?.data?.message || error.message,
            timestamp: new Date().toISOString()
          }
        }));
      }

      toast.success('Testes concluídos!');
    } catch (error: any) {
      toast.error('Erro ao executar testes');
      console.error('Erro nos testes:', error);
    }
  };

  const getStatusIcon = (success: boolean) => {
    if (success === undefined) return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    return success ? 
      <CheckCircle className="h-5 w-5 text-green-500" /> : 
      <XCircle className="h-5 w-5 text-red-500" />;
  };

  const getStatusText = (success: boolean) => {
    if (success === undefined) return 'Não testado';
    return success ? 'Sucesso' : 'Falha';
  };

  return (
          <div className="p-6 space-y-6" data-testid="page-test">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Teste do Sistema</h1>
          <p className="text-muted-foreground">
            Verifique se todas as APIs estão funcionando corretamente
          </p>
        </div>
        <Button onClick={runTests} disabled={healthLoading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Executar Testes
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Health Check */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Health Check</CardTitle>
              {getStatusIcon(testResults.health?.success)}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-2">
              Status: {getStatusText(testResults.health?.success)}
            </p>
            {testResults.health?.data && (
              <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                {JSON.stringify(testResults.health.data, null, 2)}
              </pre>
            )}
            {testResults.health?.error && (
              <p className="text-xs text-red-600">{testResults.health.error}</p>
            )}
          </CardContent>
        </Card>

        {/* Login */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Autenticação</CardTitle>
              {getStatusIcon(testResults.login?.success)}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-2">
              Status: {getStatusText(testResults.login?.success)}
            </p>
            {testResults.login?.data && (
              <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                {JSON.stringify(testResults.login.data, null, 2)}
              </pre>
            )}
            {testResults.login?.error && (
              <p className="text-xs text-red-600">{testResults.login.error}</p>
            )}
          </CardContent>
        </Card>

        {/* Kanban */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">API Kanban</CardTitle>
              {getStatusIcon(testResults.kanban?.success)}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-2">
              Status: {getStatusText(testResults.kanban?.success)}
            </p>
            {testResults.kanban?.data && (
              <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                {JSON.stringify(testResults.kanban.data, null, 2)}
              </pre>
            )}
            {testResults.kanban?.error && (
              <p className="text-xs text-red-600">{testResults.kanban.error}</p>
            )}
          </CardContent>
        </Card>

        {/* Clientes */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">API Clientes</CardTitle>
              {getStatusIcon(testResults.clientes?.success)}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-2">
              Status: {getStatusText(testResults.clientes?.success)}
            </p>
            {testResults.clientes?.data && (
              <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                {JSON.stringify(testResults.clientes.data, null, 2)}
              </pre>
            )}
            {testResults.clientes?.error && (
              <p className="text-xs text-red-600">{testResults.clientes.error}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resumo */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo dos Testes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.entries(testResults).map(([testName, result]: [string, any]) => (
              <div key={testName} className="flex items-center justify-between p-2 bg-muted rounded">
                <span className="font-medium capitalize">{testName}</span>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(result.success)}
                  <span className="text-sm">{getStatusText(result.success)}</span>
                  {result.timestamp && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(result.timestamp).toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {Object.keys(testResults).length === 0 && (
            <p className="text-center text-muted-foreground py-4">
              Execute os testes para ver os resultados
            </p>
          )}
        </CardContent>
      </Card>

      {/* Debug Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informações de Debug</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Health Check (React Query)</h4>
              <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                {JSON.stringify({
                  data: healthData,
                  isLoading: healthLoading
                }, null, 2)}
              </pre>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Configuração da API</h4>
              <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                {JSON.stringify({
                  baseURL: api.defaults.baseURL,
                  timeout: api.defaults.timeout,
                  headers: api.defaults.headers
                }, null, 2)}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
